import { ProfileBaseComponent } from "./profile-base-component.js";
/**
 * Serve per creare una istanza di Choices.js in maniera centralizzata
 */
/**
 * Il tipo di dato che questo componente gestisce
*/
import type { ProfileAttribute } from "@bonusmap/api-types";
// lo schema zod per validare i dati se serve
import { ProfileAttributeComponent } from "./profile-attribute.js";
import { IProfileAttributes } from "./IProfileAttributes.js";
import { Delegate } from "@bonusmap/shared_ui/library";
import { toastWarning } from "../../toasts.js";
import { ProfileAttributeItem } from "./profile-attribute-item.js";
import { ProfileAttributeValue } from "./profile-attribute-value.js";

export class ProfileAttributes extends ProfileBaseComponent implements IProfileAttributes {
    static webComponentName: string = "profile-attributes";
    static observedAttributes = []; // Nessun attributo da osservare per ora
    protected _templateId: string = "profile-attributes";
    private addAttributeLink!: HTMLAnchorElement;
    private listContainerEl!: HTMLElement;
    private noElementsEl!: HTMLElement;
    private _componentId!: string;
    public get componentId(): string {
        if (!this._componentId) {
            this._componentId = `${this.createRandomId()}`;
        }
        return this._componentId;
    }



    private _value: ProfileAttribute[] = [];
    get value(): ProfileAttribute[] {
        return this._value;
    }
    set value(newValue: ProfileAttribute[]) {
        if (Array.isArray(newValue)) {
            const validValues = newValue.filter(item => this.isValidProfileAttribute(item));
            // Notifica il cambiamento di valore solo se effettivamente cambia
            if (JSON.stringify(this._value) !== JSON.stringify(validValues)) {
                this.dispatch('value-changed', {
                    value: validValues
                });
            }

            this._value = validValues
        }
    }
    private _profileAttributeControls: ProfileAttributeComponent[] = [];
    public get profileAttributeControls(): ProfileAttributeComponent[] {
        return this._profileAttributeControls;
    }

    public set profileAttributeControls(value: ProfileAttributeComponent[]) {
        this._profileAttributeControls = value;
        // Sincronizza il valore con gli attributi figli validi
        const newValues = this.profileAttributeControlsValids.map(item => item.value);
        this.value = newValues;
    }
    public get profileAttributeControlsValids(): ProfileAttributeComponent[] {
        return this._profileAttributeControls.filter(item => item.valid);
    }
    public get profileAttributeControlsNotValids(): ProfileAttributeComponent[] {
        return this._profileAttributeControls.filter(item => !item.valid);
    }
    public get ids(): number[] {
        return this.profileAttributeControlsValids.map(item => item.value.attribute_id);
    }
    createProfileAttributeControl(): void {
        this.refreshControls();
        if (this.profileAttributeControlsNotValids.length > 0) {
            // Non creare un nuovo controllo se c'è già uno non valido
            toastWarning("Completa o rimuovi tutti gli attributi non completati prima di aggiungerne un altro.");
            return;
        }
        const profileAttribute = document.createElement('profile-attribute');
        this.listContainerEl.querySelector('[role="no-elements"]')?.remove();
        this.listContainerEl.appendChild(profileAttribute);
        this.profileAttributeControls = [...this.profileAttributeControls, profileAttribute as ProfileAttributeComponent];
    }
    protected onConnected(): void {
        // Inizializza il componente
        const scriptsrc = this.querySelector('script');
        if (scriptsrc) {
            const scriptText = scriptsrc.textContent || '';
            try {
                const parsed = JSON.parse(scriptText);
                console.log("ProfileAttributes: dati iniziali", parsed);
                if (Array.isArray(parsed)) {
                    this.value = parsed;
                }
            } catch (error) {
                console.error("ProfileAttributes: errore nel parsing del JSON", error);
            }
            scriptsrc.remove();
        }
        // Ascolta gli eventi di rimozione dai figli
        this.addEventListener('remove-attribute', (event: any) => {
            const origin = event.detail.origin as ProfileAttributeComponent;
            origin.remove();
            this.profileAttributeControls = this.profileAttributeControls.filter(item => item !== origin);
            // Mostra o nasconde il messaggio di nessun elemento
            if (this.profileAttributeControls.length === 0) {
                this.render();
            }
            event.stopPropagation();
        })
        this.addEventListener('value-changed', (event: any) => {
            if (event.target !== this) {
                event.stopPropagation();
                if (this.rendered) {
                    this.refreshControls();
                    this.profileAttributeControls = [...this.profileAttributeControls]; // Trigger the setter to update value
                }
            }
        });
        const globalDelegator = new Delegate(document.body);
        globalDelegator.on('click', 'a[role="add-attribute"]', (event: Event) => {
            event.preventDefault();
            this.createProfileAttributeControl();
        });

    }
    protected refreshControls(): void {
        const profileAttributeElements = this.querySelectorAll('profile-attribute');
        this.profileAttributeControls = Array.from(profileAttributeElements) as ProfileAttributeComponent[];
    }
    buildRenderingData(): Record<string, unknown> {
        return {
            componentId: this.componentId,
            items: this.value,
            readOnly: this.readOnly
        };
    }
    private rendered = false;
    protected afterRender(): void {
        this.refreshControls();
        this.listContainerEl = (this.roleElementSelector('list-container') || this) as HTMLElement;
        this.noElementsEl = this.roleElementSelector('no-elements') as HTMLElement;
        this.rendered = true;
    }

    protected render(): void {
        super.render();
    }
    static registerElement(): void {
        super.registerElement();
        ProfileAttributeComponent.registerElement();
        ProfileAttributeItem.registerElement();
        ProfileAttributeValue.registerElement();
    }

}